import type { EntityType } from "@config";

/**
 * Max containment depth for nested repeatable groups, per the repeatable
 * design (Figma 642:45250): slide ⊃ combo ⊃ set is the deepest supported
 * chain. Marking an entity whose ancestor chain already has this many
 * repeatable markers would create a 4th nested level and must be blocked.
 */
export const MAX_REPEATABLE_NESTING_DEPTH = 3;

/**
 * Counts how many ancestors of `entityId` (walking up `parentId`, NOT
 * including the entity itself) are already marked repeatable
 * (`settings.repeatableKey` set). Cycle-safe: stops if a parent chain
 * revisits an id (shouldn't happen, but guards against corrupt data
 * producing an infinite loop).
 */
export const countRepeatableAncestors = (
  entityId: string,
  entities: Record<string, EntityType>,
): number => {
  let count = 0;
  const seen = new Set<string>();
  let currentId = entities[entityId]?.parentId ?? null;
  while (currentId && entities[currentId] && !seen.has(currentId)) {
    seen.add(currentId);
    if (entities[currentId].settings?.repeatableKey) count++;
    currentId = entities[currentId].parentId;
  }
  return count;
};

/**
 * True when marking `entityId` repeatable would exceed the max nesting
 * depth — i.e. its ancestor chain already has `maxDepth` repeatable
 * markers, so this mark would create the (maxDepth + 1)-th nested level.
 */
export const wouldExceedRepeatableNestingDepth = (
  entityId: string,
  entities: Record<string, EntityType>,
  maxDepth: number = MAX_REPEATABLE_NESTING_DEPTH,
): boolean => countRepeatableAncestors(entityId, entities) >= maxDepth;

/**
 * Key of the nearest repeatable group ABOVE `entityId` (strict ancestors only),
 * or null when the entity is not nested inside another group.
 *
 * Drives token re-parking: a token belongs to its nearest enclosing group, so
 * marking a nested entity takes over the ancestor's tokens inside it, and
 * un-marking hands them back to this ancestor.
 */
export const findAncestorRepeatableKey = (
  entityId: string,
  entities: Record<string, EntityType>,
): string | null => {
  const seen = new Set<string>();
  let currentId = entities[entityId]?.parentId ?? null;
  while (currentId && entities[currentId] && !seen.has(currentId)) {
    seen.add(currentId);
    const key = entities[currentId].settings?.repeatableKey;
    if (typeof key === "string" && key) return key;
    currentId = entities[currentId].parentId;
  }
  return null;
};
